<template>
  <div class="overflow-x-hidden relative">
    <!-- Hoş geldin mesajı -->
    <transition name="fade">
      <div
        v-if="showWelcome"
        class="fixed top-6 left-6 bg-white border border-gray-300 shadow-lg rounded-xl px-5 py-3 z-[9999] text-gray-800 flex items-center justify-between gap-4 min-w-[280px]"
      >
        <span>👋 Merhaba {{ user.name }} {{ user.surname }}! Hoş geldiniz.</span>
        <button @click="closeWelcome" class="text-gray-500 hover:text-gray-800 text-lg leading-none">
          &times;
        </button>
      </div>
    </transition>

    <!-- ✅ Custom Slider -->
    <div class="relative w-full overflow-hidden">
      <CustomSlider />
      <div class="relative z-30 -mt-14">
        <CtaBanner />
      </div>
    </div>

    <!-- Nasıl Çalışır (yatay, kart içinde) -->
    <section class="how-section">
      <div class="how-container">
        <h2 class="how-title" data-aos="fade-up">
          {{ $t('howItWorks.title') }}
        </h2>
        <div class="how-steps">
          <div
            v-for="(step, index) in steps"
            :key="index"
            class="how-step"
            data-aos="zoom-in"
            :data-aos-delay="index * 100"
          >
            <div class="icon-wrapper">
              <img :src="step.icon" alt="icon" />
            </div>
            <h4>{{ $t(step.title) }}</h4>
            <p>{{ $t(step.desc) }}</p>
          </div>
        </div>
      </div>
    </section>

          <!-- Hizmetler -->
      <section class="section-wrapper bg-white/70 backdrop-blur-md">
        <div class="container">
          <h2 class="section-title card-title">{{ $t('services.title') }}</h2>
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            <div
              v-for="(service, index) in services"
              :key="index"
              class="service-card p-6 rounded-xl  shadow-md"
              :data-aos="'zoom-in'"
              :data-aos-delay="index * 150"
            >
              <div class="text-blue-600 text-4xl mb-4">
                <i :class="service.icon"></i>
              </div>
              <h3 class="text-lg font-semibold text-gray-800 mb-2">
                {{ $t(service.title) }}
              </h3>
              <p class="text-sm text-gray-600 mb-4">
                {{ $t(service.description) }}
              </p>

            <!-- Buton sadece buttonText boş değilse gösterilsin -->
            <router-link
              v-if="$t(service.buttonText).trim() !== ''"
              :to="service.route"
            >
              <button class="btn-primary">{{ $t(service.buttonText) }}</button>
            </router-link>
            </div>
          </div>
        </div>
      </section>

    <!-- Yorumlar -->
    <section class="section-wrapper testimonials-horizontal">
      <div class="container">
        <h2 class="section-title">{{ $t('testimonials.title') }}</h2>
        <div class="testimonials-list">
          <div
            v-for="(t, index) in testimonials"
            :key="index"
            class="testimonial-card"
            data-aos="zoom-in"
            :data-aos-delay="index * 150"
          >
            <div class="stars mb-3">
              <i v-for="n in t.stars" :key="'filled-' + n" class="fas fa-star"></i>
              <i v-for="n in 5 - t.stars" :key="'empty-' + n" class="far fa-star text-gray-300"></i>
            </div>
            <p class="comment">"{{ $t(t.comment) }}"</p>
            <p class="author">- {{ $t(t.name) }}</p>
          </div>
        </div>

        <div class="share-feedback">
          <h3 class="share-thoughts">Sende Düşüncelerini Bizimle Paylaş</h3>

          <label for="nameInput" class="label-name">İsminiz:</label>
          <input
            id="nameInput"
            type="text"
            v-model="nameInput"
            :readonly="user !== null"
            placeholder="İsminizi yazınız..."
            class="input-name"
          />

          <div class="star-rating">
            <span
              v-for="star in 5"
              :key="star"
              class="star"
              :class="{ filled: star <= (hoverRating || rating) }"
              @click="setRating(star)"
              @mouseover="hoverRating = star"
              @mouseleave="hoverRating = 0"
            >
              ★
            </span>
          </div>

          <textarea
            v-model="comment"
            class="share-textarea"
            placeholder="Yorumunuzu buraya yazın..."
          ></textarea>

          <button class="share-button" @click="submitComment">Gönder</button>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getUser } from '@/utils/auth'
import CustomSlider from '@/components/CustomSlider.vue'
import CtaBanner from '@/components/CtaBanner.vue'
import AOS from 'aos'
import 'aos/dist/aos.css'
import axios from 'axios'

const user = ref(getUser())
const showWelcome = ref(false)
const rating = ref(0)
const hoverRating = ref(0)
const comment = ref('')
const nameInput = ref(user.value?.name || '')  // Kullanıcı adı varsa başta doldur

const panelRoute = computed(() => {
  if (!user.value) return '/'
  if (user.value.role === 'admin') return '/panel/admin'
  if (user.value.role === 'translator') return '/panel/translator'
  return '/panel/user'
})

function closeWelcome() {
  showWelcome.value = false
  localStorage.setItem('welcome_shown', '1')
}

function setRating(star) {
  rating.value = star
}

async function submitComment() {
  if (rating.value === 0) {
    alert('Lütfen bir puan seçin!')
    return
  }
  if (!comment.value.trim()) {
    alert('Lütfen yorumunuzu yazın!')
    return
  }
  if (!nameInput.value.trim()) {
    alert('Lütfen isminizi yazın!')
    return
  }
  try {
    const payload = {
      name: nameInput.value,
      comment: comment.value,
      stars: rating.value
    }
    const response = await axios.post('/api/testimonials', payload)
    alert('Yorumunuz başarıyla gönderildi.')
    comment.value = ''
    rating.value = 0
    if (!user.value) nameInput.value = ''  // Giriş yapmamışsa alanı temizle
    // İstersen burada yorum listesini güncelleyebilirsin
  } catch (error) {
    alert('Yorum gönderilirken hata oluştu.')
    console.error(error)
  }
}

onMounted(() => {
  document.title = "Osmanlıca Belge Tercümesi | Güvenilir ve Hızlı Tercüme"

  const description = document.createElement('meta')
  description.name = "description"
  description.content = "Osmanlıca belgeleriniz uzman kadromuz tarafından kısa sürede tercüme edilir. Güvenilir ve noter onaylı hizmet."
  document.head.appendChild(description)

  const keywords = document.createElement('meta')
  keywords.name = "keywords"
  keywords.content = "Osmanlıca tercüme, belge çevirisi, noter yeminli tercüme, Osmanlıca çeviri, tarihi belge çevirisi"
  document.head.appendChild(keywords)

  const ogTitle = document.createElement('meta')
  ogTitle.setAttribute('property', 'og:title')
  ogTitle.setAttribute('content', 'Osmanlıca Belge Tercümesi | Osmanlıca Belgeler')
  document.head.appendChild(ogTitle)

  const ogDesc = document.createElement('meta')
  ogDesc.setAttribute('property', 'og:description')
  ogDesc.setAttribute('content', 'Osmanlıca belgelerinizi hızlı ve güvenilir şekilde çevirtin. Noter onaylı tercümanlarımızla hizmetinizdeyiz.')
  document.head.appendChild(ogDesc)

  const canonical = document.createElement('link')
  canonical.setAttribute('rel', 'canonical')
  canonical.setAttribute('href', 'https://osmanlicabelgeler.com/')
  document.head.appendChild(canonical)
  
  AOS.init({
    duration: 800,
    once: true,
    offset: 200,
    easing: 'ease-in-out'
  })

  if (user.value && !localStorage.getItem('welcome_shown')) {
    showWelcome.value = true
    localStorage.setItem('welcome_shown', '1')
    setTimeout(() => {
      showWelcome.value = false
    }, 5000)
  }
})

// Hizmetler – Güncellenmiş
const services = [
  {
    icon: 'fas fa-file-alt',
    title: 'services.official.title',
    description: 'services.official.description',
    buttonText: 'services.official.button',
    route: '/quote'
  },
  {
    icon: 'fas fa-pen-nib',
    title: 'services.sworn.title',
    description: 'services.sworn.description',
    buttonText: 'services.sworn.button',
    route: '/quote'
  },
  {
    icon: 'fas fa-stamp',
    title: 'services.notary.title',
    description: 'services.notary.description',
    buttonText: 'services.notary.button',
    route: '/quote'
  },
  {
    icon: 'fas fa-globe-europe',
    title: 'services.apostille.title',
    description: 'services.apostille.description',
    buttonText: 'services.apostille.button',
    route: '/quote'
  },
  {
    icon: 'fas fa-sync-alt',
    title: 'services.revision.title',
    description: 'services.revision.description',
    buttonText: 'services.revision.button',
    route: '/login'
  },
  {
    icon: 'fas fa-bell',
    title: 'services.notifications.title',
    description: 'services.notifications.description',
    buttonText: 'services.notifications.button',
    route: '/settings'
  }
]

// Nasıl Çalışır adımları
const steps = [
  { icon: '/icons/upload.svg', title: 'howItWorks.upload', desc: 'howItWorks.uploadDesc' },
  { icon: '/icons/offer.svg', title: 'howItWorks.offer', desc: 'howItWorks.offerDesc' },
  { icon: '/icons/payment.svg', title: 'howItWorks.pay', desc: 'howItWorks.payDesc' },
  { icon: '/icons/download.svg', title: 'howItWorks.receive', desc: 'howItWorks.receiveDesc' }
]

// Yorumlar
const testimonials = [
  {
    comment: 'testimonials.comment1',
    name: 'testimonials.name1',
    stars: 5
  },
  {
    comment: 'testimonials.comment2',
    name: 'testimonials.name2',
    stars: 4
  },
  {
    comment: 'testimonials.comment3',
    name: 'testimonials.name3',
    stars: 5
  },
  {
    comment: 'testimonials.comment4',
    name: 'testimonials.name4',
    stars: 5
  }
]
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600&display=swap');

.container {
  max-width: 1200px;
  margin: auto;
  padding: 20px;
}

.section-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 36px;
  text-align: center;
  margin-bottom: 30px;
  padding: 20px 30px;
  background-color: rgba(255, 255, 255, 0.87);
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  display: inline-block;
}

.section-wrapper {
  padding: 80px 20px;
  margin-bottom: 40px;
  border-radius: 12px;
  
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.service-card {
  background-color: rgba(252, 250, 250, 0.87);
  border-radius: 10px;
  padding: 24px;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.service-card:hover {
  transform: translateY(-5px) scale(1.05);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.service-card h3 {
  font-size: 1.8rem; /* Daha büyük yazı */
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  color: #222222;
  margin-bottom: 0.75rem;
}
.service-card p {
  font-size: 1.1rem;
  font-family: 'Roboto', sans-serif;
  color: #1b1b1b;
  line-height: 1.6;
  margin-bottom: 1.2rem;
}

.btn-primary {
  background-color: #2563eb;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.3s ease;
  font-family: 'Poppins', sans-serif;
}

.btn-primary:hover {
  background-color: #1e40af;
}

.testimonials-horizontal {
  background-color: rgba(255, 255, 255, 0);
  backdrop-filter: blur(px);
  padding: 40px 20px;
  border-radius: 14px;
  max-width: 1400px;
  margin: 0 auto 60px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.testimonials-list {
  display: flex;
  gap: 16px;
  flex-wrap: nowrap;
  overflow-x: visible;
  padding-bottom: 10px;
}

.testimonial-card {
  background-color: rgba(255, 255, 255, 0.87);
  border-radius: 14px;
  padding: 25px 32px;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: default;
  flex: 0 0 280px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.testimonial-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 32px rgba(0, 0, 0, 0.18);
}

.stars {
  color: #facc15;
  margin-bottom: 16px;
  font-size: 18px;
}
.comment {
  font-style: italic;
  color: #3a3a3a;
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 22px;
}
.author {
  text-align: right;
  font-weight: 700;
  color: #222;
  font-size: 16px;
}

.share-feedback {
  max-width: 600px;
  margin: 40px auto 0;
  background: rgba(255, 255, 255, 0.87);
  padding: 20px 30px;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
  text-align: center;
  font-family: 'Poppins', sans-serif;
}

.share-thoughts {
  font-weight: 700;
  font-size: 22px;
  margin-bottom: 18px;
  color: #222;
}
.label-name {
  display: block;
  font-weight: 600;
  text-align: left;
  margin-bottom: 6px;
  font-size: 16px;
  color: #333;
}
.input-name {
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
  margin-bottom: 18px;
  box-sizing: border-box;
}
.input-name[readonly] {
  background-color: #f5f5f5;
  cursor: not-allowed;
}
.star-rating {
  margin-bottom: 18px;
  font-size: 32px;
  user-select: none;
}
.star {
  cursor: pointer;
  color: #ccc;
  transition: color 0.3s ease;
  padding: 0 4px;
}
.star.filled {
  color: #facc15;
}
.star:hover,
.star:hover ~ .star {
  color: #facc15;
}
.share-textarea {
  width: 100%;
  height: 100px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 16px;
  resize: vertical;
  box-sizing: border-box;
  margin-bottom: 20px;
}
.share-button {
  background-color: #2563eb;
  color: white;
  font-weight: 600;
  font-size: 16px;
  padding: 10px 26px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}
.share-button:hover {
  background-color: #1e40af;
}

.how-section {
  padding: 80px 20px;
}
.how-container {
  max-width: 1100px;
  margin: auto;
  background: rgba(255, 255, 255, 0.87);
  padding: 30px 30px 40px;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  text-align: center;
}
.how-title {
  font-size: 35px;
  font-weight: 700;
  color: #222;
  margin-bottom: 25px;
}
.how-steps {
  display: flex;
  justify-content: space-around;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 30px;
}
.how-step {
  width: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.how-step h4 {
  margin-top: 15px;
  font-size: 20px;
  font-weight: 700;
  color: #333;
}
.how-step p {
  font-size: 16px;
  color: #222121;
  margin-top: 8px;
  line-height: 1.4;
}
.icon-wrapper {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}
.icon-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
@media (max-width: 600px) {
  .section-title {
    font-size: 22px;
    padding: 14px 20px;
  }

  .how-container {
    padding: 20px;
  }

  .how-title {
    font-size: 24px;
  }

  .how-steps {
    flex-direction: column;
    align-items: center;
  }

  .how-step {
    width: 100%;
    max-width: 280px;
  }

  .service-card {
    padding: 16px;
  }

  .service-card h3 {
    font-size: 18px;
  }

  .service-card p {
    font-size: 14px;
  }

  .testimonials-list {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 10px;
  }

  .testimonial-card {
    flex: 0 0 240px;
    font-size: 14px;
  }

  .comment {
    font-size: 14px;
  }

  .author {
    font-size: 13px;
  }

  .share-feedback {
    padding: 20px;
  }

  .share-thoughts {
    font-size: 18px;
  }

  .input-name,
  .share-textarea {
    font-size: 14px;
  }

  .share-button {
    font-size: 14px;
    padding: 10px 20px;
  }
}

</style>
